import { cursorPaginationSchema } from './paginationSchema';
import type { JsonObject, JsonValue } from "@inf-monkeys-tech/monkeys";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useState,
} from "react";

import {
  BaseButton,
  BaseAccordion,
  BaseBadge,
  BaseCheckbox,
  BaseDialog,
  BaseField,
  BaseFieldset,
  BaseInput,
  BaseInline,
  BaseSelect,
  BaseStack,
  BaseSwitch,
  BaseTextarea,
  BaseToolbar,
  BaseTabs,
} from "../base";
import { CapabilityStatus } from "./CapabilityStatus";
import type {
  CapabilityTranslate,
  DesignCapabilityRenderer,
} from "./contracts";
import { isJsonObject } from "./contracts";
import { DESIGN_CAPABILITY_I18N_KEYS } from "./i18n";
import {
  normalizeOntologyMarkerTemplates,
  OntologyMarkerEditor,
  type MarkerTagCatalog,
} from "./ontologyMarkerEditor";

const JSON_SCHEMA_DIALECT = "https://json-schema.org/draft/2020-12/schema";

const jsonSchema = (id: string, definition: JsonObject): JsonObject => ({
  $schema: JSON_SCHEMA_DIALECT,
  $id: id,
  ...definition,
});

const optionalText = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const requiredText = (value: unknown, name: string) => {
  const normalized = optionalText(value);
  if (!normalized) throw new TypeError(`${name} is required.`);
  return normalized;
};

const optionalNonnegativeInteger = (value: unknown, name: string) => {
  const normalized = optionalText(value);
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new TypeError(`${name} must be a nonnegative integer.`);
  }
  return parsed;
};

const optionalBoundedInteger = (
  value: unknown,
  name: string,
  minimum: number,
  maximum: number,
) => {
  const normalized = optionalText(value);
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isSafeInteger(parsed) || parsed < minimum || parsed > maximum) {
    throw new TypeError(
      `${name} must be an integer between ${minimum} and ${maximum}.`,
    );
  }
  return parsed;
};

const includeText = (target: JsonObject, name: string, value: unknown) => {
  const normalized = optionalText(value);
  if (normalized) target[name] = normalized;
};

const optionalJsonObject = (value: unknown, name: string) => {
  const normalized = optionalText(value);
  if (!normalized) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(normalized);
  } catch {
    throw new TypeError(`${name} must be valid JSON.`);
  }
  if (!isJsonObject(parsed)) {
    throw new TypeError(`${name} must be a JSON object.`);
  }
  return parsed;
};

const optionalJsonObjectOrNull = (value: unknown, name: string) => {
  const normalized = optionalText(value);
  if (!normalized) return undefined;
  if (normalized === "null") return null;
  return optionalJsonObject(normalized, name);
};

const requiredModelVersion = (model: JsonObject, name: string) => {
  const value = Number(model[name]);
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError(`${name} is required.`);
  }
  return value;
};

const optionalCsv = (value: unknown, name: string, maximum: number) => {
  const items = commaSeparatedValues(value);
  if (items.length > maximum) {
    throw new TypeError(`${name} must contain at most ${maximum} values.`);
  }
  if (items.some((item) => item.length > 256)) {
    throw new TypeError(`${name} values must contain at most 256 characters.`);
  }
  return items.length ? items : undefined;
};

const optionalNullableNonnegativeInteger = (value: unknown, name: string) => {
  const normalized = optionalText(value);
  if (!normalized) return undefined;
  if (normalized === "null") return null;
  return optionalNonnegativeInteger(normalized, name);
};

const requiredBoolean = (value: unknown, name: string) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  throw new TypeError(`${name} must be true or false.`);
};

export const ONTOLOGY_MANAGEMENT_SCHEMA =
  "monkeys-design://ontology-editor/management-actions/v6";

export const ONTOLOGY_MANAGEMENT_ACTIONS = Object.freeze([
  "ontology-create",
  "ontology-update",
  "ontology-delete",
  "ontology-marker",
  "ontology-default-views",
  "ontology-snapshot",
  "ontology-import-jsonl",
  "ontology-import-records-jsonl",
  "ontology-import-tag-relations-jsonl",
  "ontology-import-text-column-jsonl",
  "ontology-import-json-column-jsonl",
  "ontology-import-relation-column-jsonl",
  "feature-column-create",
  "feature-column-update",
  "feature-column-attachment-set",
  "view-export-jsonl",
  "feature-column-images-export",
  "view-create",
  "view-update",
  "view-delete",
  "view-share",
] as const);

export type OntologyManagementAction =
  (typeof ONTOLOGY_MANAGEMENT_ACTIONS)[number];

const actionIntentRef = (action: OntologyManagementAction) =>
  `monkeys-design://intent/ontology-management-${action}/v6`;

export const ONTOLOGY_MANAGEMENT_RENDER_MODEL_SCHEMA = jsonSchema(
  ONTOLOGY_MANAGEMENT_SCHEMA,
  {
    type: "object",
    additionalProperties: false,
    required: ["resourceKind", "action", "contextId"],
    properties: {
      title: { type: "string" },
      titleKey: { type: "string", minLength: 1 },
      description: { type: "string" },
      descriptionKey: { type: "string", minLength: 1 },
      submitLabelKey: { type: "string", minLength: 1 },
      cancelLabelKey: { type: "string", minLength: 1 },
      resourceKind: {
        enum: ["ontology", "view", "feature-column", "export"],
      },
      action: { enum: [...ONTOLOGY_MANAGEMENT_ACTIONS] },
      contextId: { type: "string", minLength: 1, maxLength: 256 },
      tagSearch: { type: "string", maxLength: 512 },
      pagination: cursorPaginationSchema(),
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      expectedOntologyDefinitionVersion: { type: "integer", minimum: 1 },
      expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
      markerVersion: { type: "integer", minimum: 0 },
      markerPresets: {
        type: "array",
        maxItems: 16,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "labelKey", "templates"],
          properties: {
            id: { type: "string", minLength: 1, maxLength: 128 },
            labelKey: { type: "string", minLength: 1, maxLength: 256 },
            templates: {
              type: "array",
              maxItems: 64,
              items: { type: "object", additionalProperties: true },
            },
          },
        },
      },
      newMarkerTemplate: {
        type: "object",
        additionalProperties: false,
        properties: {
          keyPrefix: { type: "string", maxLength: 64 },
          namePrefix: { type: "string", maxLength: 128 },
          viewType: { type: "string", maxLength: 64 },
        },
      },
      defaults: {
        type: "object",
        additionalProperties: false,
        properties: {
          displayName: { type: "string" },
          description: { type: "string" },
          bodyKind: { type: "string" },
          visible: { type: "boolean" },
          publicRead: { type: "boolean" },
          publicWrite: { type: "boolean" },
          kernelReadAllTeams: { type: "boolean" },
          customizeViewModes: { type: "boolean" },
          availableViewModes: {
            type: "array",
            items: { type: "string" },
          },
          defaultViewMode: { type: "string" },
          viewModeOptions: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["id", "labelKey"],
              properties: {
                id: { type: "string", minLength: 1 },
                labelKey: { type: "string", minLength: 1 },
              },
            },
          },
          profile: { type: "string" },
          sourceOntologyId: { type: "string" },
          viewId: { type: "string" },
          name: { type: "string" },
          indexMode: { type: "string" },
          parentId: { type: "string" },
          viewType: { enum: ["filter", "container"] },
          sourceContainerViewId: { type: "string" },
          displayConfig: {
            type: "object",
            additionalProperties: true,
          },
          sort: { type: "integer", minimum: 0 },
          managedFileId: { type: "string" },
          mode: { type: "string" },
          columnId: { type: "string" },
          containerViewId: { type: "string" },
          type: { type: "string" },
          config: { type: ["object", "null"], additionalProperties: true },
          scopeContainerViewId: { type: "string" },
          attached: { type: "boolean" },
          pageSize: { type: "integer", minimum: 1 },
          tagGroupIds: { type: "string" },
          featureColumnIds: { type: "string" },
          basicFieldIds: { type: "string" },
          columnType: { enum: ["feature", "preview"] },
          featureColumnId: { type: "string" },
          itemLimit: { type: "integer", minimum: 1 },
          shareOperation: { enum: ["upsert", "revoke"] },
          targetType: { enum: ["user", "team"] },
          targetId: { type: "string" },
          permissions: { type: "string" },
          expiresAt: { type: "integer", minimum: 1 },
          markerExists: { type: "boolean" },
          markerVersion: { type: "integer", minimum: 0 },
          markerLabel: { type: "string", maxLength: 128 },
          markerLabelKey: { type: "string", minLength: 1, maxLength: 256 },
          defaultViewTemplates: {
            type: "array",
            maxItems: 64,
            items: { type: "object", additionalProperties: true },
          },
          tagChoices: {
            type: "array",
            maxItems: 500,
            items: {
              type: "object",
              additionalProperties: false,
              required: ["id", "label"],
              properties: {
                id: { type: "string", minLength: 1, maxLength: 256 },
                label: { type: "string", minLength: 1, maxLength: 512 },
                color: { type: "string", maxLength: 128 },
                groupId: { type: "string", minLength: 1, maxLength: 256 },
              },
            },
          },
          tagGroupChoices: {
            type: "array",
            maxItems: 500,
            items: {
              type: "object",
              additionalProperties: false,
              required: ["id", "label"],
              properties: {
                id: { type: "string", minLength: 1, maxLength: 256 },
                label: { type: "string", minLength: 1, maxLength: 512 },
              },
            },
          },
        },
      },
      submitting: { type: "boolean" },
      disabled: { type: "boolean" },
      errorCode: { type: "string", minLength: 1, maxLength: 128 },
    },
    allOf: [
      {
        if: {
          properties: { action: { const: "ontology-marker" } },
          required: ["action"],
        },
        then: { required: ["markerVersion"] },
      },
      {
        if: {
          properties: {
            action: {
              enum: [
                "ontology-update",
                "ontology-delete",
                "ontology-marker",
                "ontology-default-views",
                "view-update",
                "view-delete",
                "view-share",
                "feature-column-update",
                "ontology-import-records-jsonl",
                "ontology-import-tag-relations-jsonl",
                "ontology-import-text-column-jsonl",
                "ontology-import-json-column-jsonl",
                "ontology-import-relation-column-jsonl",
              ],
            },
          },
          required: ["action"],
        },
        then: { required: ["expectedDefinitionVersion"] },
      },
      {
        if: {
          properties: { action: { const: "feature-column-create" } },
          required: ["action"],
        },
        then: { required: ["expectedOntologyDefinitionVersion"] },
      },
      {
        if: {
          properties: {
            action: {
              enum: [
                "feature-column-attachment-set",
                "view-export-jsonl",
                "feature-column-images-export",
              ],
            },
          },
          required: ["action"],
        },
        then: { required: ["expectedViewDefinitionVersion"] },
      },
    ],
  },
);

const strictIntent = (
  action: OntologyManagementAction,
  properties: JsonObject,
  required: string[],
) =>
  jsonSchema(actionIntentRef(action), {
    type: "object",
    additionalProperties: false,
    properties,
    ...(required.length ? { required } : {}),
  });

export const ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS = Object.freeze({
  cancel: jsonSchema("monkeys-design://intent/ontology-management-cancel/v6", {
    type: "object",
    additionalProperties: false,
    properties: {},
  }),
  ontologyCreate: strictIntent(
    "ontology-create",
    {
      displayName: { type: "string", minLength: 1, maxLength: 512 },
      description: { type: "string", maxLength: 8192 },
      bodyKind: { type: "string", minLength: 1, maxLength: 256 },
      visible: { type: "boolean" },
      publicRead: { type: "boolean" },
      publicWrite: { type: "boolean" },
      kernelReadAllTeams: { type: "boolean" },
      displayConfig: {
        type: "object",
        additionalProperties: false,
        required: ["availableViewModes", "defaultViewMode"],
        properties: {
          availableViewModes: {
            type: "array",
            minItems: 1,
            uniqueItems: true,
            items: { type: "string", minLength: 1, maxLength: 128 },
          },
          defaultViewMode: { type: "string", minLength: 1, maxLength: 128 },
        },
      },
    },
    ["displayName"],
  ),
  ontologyUpdate: strictIntent(
    "ontology-update",
    {
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      changes: {
        type: "object",
        additionalProperties: false,
        minProperties: 1,
        properties: {
          displayName: { type: "string", minLength: 1, maxLength: 512 },
          description: { type: "string", maxLength: 8192 },
          bodyKind: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
    },
    ["expectedDefinitionVersion", "changes"],
  ),
  ontologyDelete: strictIntent(
    "ontology-delete",
    { expectedDefinitionVersion: { type: "integer", minimum: 1 } },
    ["expectedDefinitionVersion"],
  ),
  ontologyMarkerSet: strictIntent(
    "ontology-marker",
    {
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      expectedVersion: { type: "integer", minimum: 0 },
      label: { type: "string", minLength: 1, maxLength: 128 },
      config: {
        type: "object",
        additionalProperties: false,
        properties: {
          defaultViewTemplates: {
            type: "array",
            maxItems: 64,
            items: { type: "object", additionalProperties: true },
          },
        },
      },
    },
    ["expectedDefinitionVersion", "expectedVersion", "label", "config"],
  ),
  ontologyMarkerDelete: jsonSchema(
    "monkeys-design://intent/ontology-management-ontology-marker-delete/v6",
    {
      type: "object",
      additionalProperties: false,
      required: ["expectedDefinitionVersion", "expectedVersion"],
      properties: { expectedDefinitionVersion: { type: "integer", minimum: 1 }, expectedVersion: { type: "integer", minimum: 1 } },
    },
  ),
  ontologyDefaultViews: strictIntent(
    "ontology-default-views",
    { expectedDefinitionVersion: { type: "integer", minimum: 1 }, profile: { type: "string", minLength: 1, maxLength: 128 } },
    ["expectedDefinitionVersion"],
  ),
  ontologySnapshot: strictIntent(
    "ontology-snapshot",
    {
      sourceOntologyId: { type: "string", minLength: 1, maxLength: 256 },
      viewId: { type: "string", minLength: 1, maxLength: 256 },
      name: { type: "string", minLength: 1, maxLength: 512 },
      indexMode: { type: "string", minLength: 1, maxLength: 128 },
    },
    ["sourceOntologyId"],
  ),
  ontologyImportJsonl: strictIntent(
    "ontology-import-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
    },
    ["managedFileRef"],
  ),
  ontologyImportRecordsJsonl: strictIntent(
    "ontology-import-records-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      mode: { type: "string", minLength: 1, maxLength: 128 },
      containerViewId: { type: "string", minLength: 1, maxLength: 256 },
    },
    ["managedFileRef", "expectedDefinitionVersion"],
  ),
  ontologyImportTagRelationsJsonl: strictIntent(
    "ontology-import-tag-relations-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      mode: { type: "string", minLength: 1, maxLength: 128 },
    },
    ["managedFileRef", "expectedDefinitionVersion"],
  ),
  ontologyImportTextColumnJsonl: strictIntent(
    "ontology-import-text-column-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      columnId: { type: "string", minLength: 1, maxLength: 256 },
    },
    ["managedFileRef", "expectedDefinitionVersion"],
  ),
  ontologyImportJsonColumnJsonl: strictIntent(
    "ontology-import-json-column-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      columnId: { type: "string", minLength: 1, maxLength: 256 },
    },
    ["managedFileRef", "expectedDefinitionVersion"],
  ),
  ontologyImportRelationColumnJsonl: strictIntent(
    "ontology-import-relation-column-jsonl",
    {
      managedFileRef: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "id"],
        properties: {
          kind: { const: "managed-file" },
          id: { type: "string", minLength: 1, maxLength: 256 },
        },
      },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      columnId: { type: "string", minLength: 1, maxLength: 256 },
    },
    ["managedFileRef", "expectedDefinitionVersion"],
  ),
  featureColumnCreate: strictIntent(
    "feature-column-create",
    {
      expectedOntologyDefinitionVersion: { type: "integer", minimum: 1 },
      name: { type: "string", minLength: 1, maxLength: 512 },
      type: { type: "string", minLength: 1, maxLength: 128 },
      config: { type: "object", additionalProperties: true },
      sort: { type: "integer", minimum: 0 },
      scopeContainerViewId: { type: "string", minLength: 1, maxLength: 256 },
    },
    ["expectedOntologyDefinitionVersion", "name"],
  ),
  featureColumnUpdate: strictIntent(
    "feature-column-update",
    {
      columnId: { type: "string", minLength: 1, maxLength: 256 },
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      changes: {
        type: "object",
        additionalProperties: false,
        minProperties: 1,
        properties: {
          name: { type: "string", minLength: 1, maxLength: 512 },
          type: { type: "string", minLength: 1, maxLength: 128 },
          config: {
            type: ["object", "null"],
            additionalProperties: true,
          },
          sort: { type: ["integer", "null"], minimum: 0 },
        },
      },
    },
    ["columnId", "expectedDefinitionVersion", "changes"],
  ),
  featureColumnAttachmentSet: strictIntent(
    "feature-column-attachment-set",
    {
      viewId: { type: "string", minLength: 1, maxLength: 256 },
      columnId: { type: "string", minLength: 1, maxLength: 256 },
      attached: { type: "boolean" },
      expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
    },
    ["viewId", "columnId", "attached", "expectedViewDefinitionVersion"],
  ),
  viewExportJsonl: strictIntent(
    "view-export-jsonl",
    {
      viewId: { type: "string", minLength: 1, maxLength: 256 },
      expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
      pageSize: { type: "integer", minimum: 100, maximum: 5000 },
      tagGroupIds: {
        type: "array",
        maxItems: 200,
        items: { type: "string", minLength: 1, maxLength: 256 },
      },
      featureColumnIds: {
        type: "array",
        maxItems: 200,
        items: { type: "string", minLength: 1, maxLength: 256 },
      },
      basicFieldIds: {
        type: "array",
        maxItems: 2,
        items: { enum: ["preview", "thumbnail"] },
      },
    },
    ["viewId", "expectedViewDefinitionVersion"],
  ),
  featureColumnImagesExport: strictIntent(
    "feature-column-images-export",
    {
      viewId: { type: "string", minLength: 1, maxLength: 256 },
      expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
      columnType: { enum: ["feature", "preview"] },
      featureColumnId: { type: "string", minLength: 1, maxLength: 256 },
      pageSize: { type: "integer", minimum: 100, maximum: 1000 },
      itemLimit: { type: "integer", minimum: 1, maximum: 1000000 },
    },
    ["viewId", "expectedViewDefinitionVersion"],
  ),
  viewCreate: strictIntent(
    "view-create",
    {
      name: { type: "string", minLength: 1, maxLength: 512 },
      description: { type: "string", maxLength: 8192 },
      parentId: { type: "string", minLength: 1, maxLength: 256 },
      viewType: { enum: ["filter", "container"] },
      sourceContainerViewId: { type: "string", minLength: 1, maxLength: 256 },
      sort: { type: "integer", minimum: 0 },
    },
    ["name"],
  ),
  viewUpdate: strictIntent(
    "view-update",
    {
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      changes: {
        type: "object",
        additionalProperties: false,
        required: ["name"],
        properties: {
          name: { type: "string", minLength: 1, maxLength: 512 },
          description: { type: "string", maxLength: 8192 },
          parentId: { type: ["string", "null"], maxLength: 256 },
          viewType: { enum: ["filter", "container"] },
          sourceContainerViewId: {
            type: ["string", "null"],
            maxLength: 256,
          },
          displayConfig: {
            type: "object",
            additionalProperties: true,
          },
          sort: { type: "integer", minimum: 0 },
        },
      },
    },
    ["expectedDefinitionVersion", "changes"],
  ),
  viewDelete: strictIntent(
    "view-delete",
    { expectedDefinitionVersion: { type: "integer", minimum: 1 } },
    ["expectedDefinitionVersion"],
  ),
  viewShare: strictIntent(
    "view-share",
    {
      expectedDefinitionVersion: { type: "integer", minimum: 1 },
      operation: { enum: ["upsert", "revoke"] },
      targetType: { enum: ["user", "team"] },
      targetId: { type: "string", minLength: 1, maxLength: 256 },
      permissions: {
        type: "array",
        maxItems: 64,
        items: { type: "string", minLength: 1, maxLength: 256 },
      },
      expiresAt: { type: "integer", minimum: 1 },
    },
    ["expectedDefinitionVersion", "operation", "targetType", "targetId"],
  ),
});

export const ONTOLOGY_MANAGEMENT_OUTPUT_PORTS = Object.freeze([
  {
    name: 'capability.ontology-management.tag-search', schemaRef: 'monkeys-design://intent/ontology-management-tag-search/v1',
    schemaDocument: jsonSchema('monkeys-design://intent/ontology-management-tag-search/v1', { type: 'object', additionalProperties: false, required: ['value'], properties: { value: { type: 'string', maxLength: 512 } } }),
    allowedCommandKinds: ['query-intent'] as const,
  },
  {
    name: 'capability.ontology-management.tags-page', schemaRef: 'monkeys-design://intent/ontology-management-tags-page/v1',
    schemaDocument: jsonSchema('monkeys-design://intent/ontology-management-tags-page/v1', { type: 'object', additionalProperties: false, required: ['page'], properties: { page: { type: 'integer', minimum: 1 } } }),
    allowedCommandKinds: ['query-intent'] as const,
  },
  {
    name: "capability.ontology-management.cancel",
    schemaRef: "monkeys-design://intent/ontology-management-cancel/v6",
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.cancel,
    allowedCommandKinds: ["query-intent"] as const,
  },
  {
    name: "capability.ontology-management.ontology.marker.set",
    schemaRef: actionIntentRef("ontology-marker"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyMarkerSet,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.marker.delete",
    schemaRef:
      "monkeys-design://intent/ontology-management-ontology-marker-delete/v6",
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyMarkerDelete,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.create",
    schemaRef: actionIntentRef("ontology-create"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyCreate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.update",
    schemaRef: actionIntentRef("ontology-update"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyUpdate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.delete",
    schemaRef: actionIntentRef("ontology-delete"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyDelete,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.default-views.ensure",
    schemaRef: actionIntentRef("ontology-default-views"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyDefaultViews,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.snapshot",
    schemaRef: actionIntentRef("ontology-snapshot"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologySnapshot,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import",
    schemaRef: actionIntentRef("ontology-import-jsonl"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import.records",
    schemaRef: actionIntentRef("ontology-import-records-jsonl"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportRecordsJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import.tag-relations",
    schemaRef: actionIntentRef("ontology-import-tag-relations-jsonl"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportTagRelationsJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import.text-column",
    schemaRef: actionIntentRef("ontology-import-text-column-jsonl"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportTextColumnJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import.json-column",
    schemaRef: actionIntentRef("ontology-import-json-column-jsonl"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportJsonColumnJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.ontology.import.relation-column",
    schemaRef: actionIntentRef("ontology-import-relation-column-jsonl"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.ontologyImportRelationColumnJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.feature-column.create",
    schemaRef: actionIntentRef("feature-column-create"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.featureColumnCreate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.feature-column.update",
    schemaRef: actionIntentRef("feature-column-update"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.featureColumnUpdate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.feature-column.attachment.set",
    schemaRef: actionIntentRef("feature-column-attachment-set"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.featureColumnAttachmentSet,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.view.export.jsonl",
    schemaRef: actionIntentRef("view-export-jsonl"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.viewExportJsonl,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.feature-column.images.export",
    schemaRef: actionIntentRef("feature-column-images-export"),
    schemaDocument:
      ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.featureColumnImagesExport,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.view.create",
    schemaRef: actionIntentRef("view-create"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.viewCreate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.view.update",
    schemaRef: actionIntentRef("view-update"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.viewUpdate,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.view.delete",
    schemaRef: actionIntentRef("view-delete"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.viewDelete,
    allowedCommandKinds: ["domain-command"] as const,
  },
  {
    name: "capability.ontology-management.view.share",
    schemaRef: actionIntentRef("view-share"),
    schemaDocument: ONTOLOGY_MANAGEMENT_INTENT_SCHEMAS.viewShare,
    allowedCommandKinds: ["domain-command"] as const,
  },
]);

export const ONTOLOGY_MANAGEMENT_INTENT_NAMES = Object.freeze({
  "ontology-create": "capability.ontology-management.ontology.create",
  "ontology-update": "capability.ontology-management.ontology.update",
  "ontology-delete": "capability.ontology-management.ontology.delete",
  "ontology-marker": "capability.ontology-management.ontology.marker.set",
  "ontology-default-views":
    "capability.ontology-management.ontology.default-views.ensure",
  "ontology-snapshot": "capability.ontology-management.ontology.snapshot",
  "ontology-import-jsonl": "capability.ontology-management.ontology.import",
  "ontology-import-records-jsonl":
    "capability.ontology-management.ontology.import.records",
  "ontology-import-tag-relations-jsonl":
    "capability.ontology-management.ontology.import.tag-relations",
  "ontology-import-text-column-jsonl":
    "capability.ontology-management.ontology.import.text-column",
  "ontology-import-json-column-jsonl":
    "capability.ontology-management.ontology.import.json-column",
  "ontology-import-relation-column-jsonl":
    "capability.ontology-management.ontology.import.relation-column",
  "feature-column-create":
    "capability.ontology-management.feature-column.create",
  "feature-column-update":
    "capability.ontology-management.feature-column.update",
  "feature-column-attachment-set":
    "capability.ontology-management.feature-column.attachment.set",
  "view-export-jsonl": "capability.ontology-management.view.export.jsonl",
  "feature-column-images-export":
    "capability.ontology-management.feature-column.images.export",
  "view-create": "capability.ontology-management.view.create",
  "view-update": "capability.ontology-management.view.update",
  "view-delete": "capability.ontology-management.view.delete",
  "view-share": "capability.ontology-management.view.share",
} satisfies Record<OntologyManagementAction, string>);

const requiredExpectedDefinitionVersion = (model: JsonObject) => {
  const value = Number(model.expectedDefinitionVersion);
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError("expectedDefinitionVersion is required.");
  }
  return value;
};

const commaSeparatedValues = (value: unknown) =>
  Array.from(
    new Set(
      String(value ?? "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );

const DEFAULT_ONTOLOGY_VIEW_MODE_OPTIONS = [
  {
    id: "table",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.viewModeTable,
  },
  {
    id: "card",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.viewModeCard,
  },
  {
    id: "gallery",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.viewModeGallery,
  },
  {
    id: "trendMatrix",
    labelKey:
      DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.viewModeTrendMatrix,
  },
];

const DEFAULT_ONTOLOGY_VIEW_MODES = DEFAULT_ONTOLOGY_VIEW_MODE_OPTIONS.slice(
  0,
  3,
).map(({ id }) => id);

export const buildOntologyManagementIntent = (
  action: OntologyManagementAction,
  model: JsonObject,
  values: Readonly<Record<string, unknown>>,
): JsonObject => {
  if (action === "ontology-create") {
    const result: JsonObject = {
      displayName: requiredText(values.displayName, "displayName"),
      visible: requiredBoolean(values.visible, "visible"),
      publicRead: requiredBoolean(values.publicRead, "publicRead"),
      publicWrite: requiredBoolean(values.publicWrite, "publicWrite"),
      kernelReadAllTeams: requiredBoolean(
        values.kernelReadAllTeams,
        "kernelReadAllTeams",
      ),
    };
    includeText(result, "description", values.description);
    includeText(result, "bodyKind", values.bodyKind);
    if (requiredBoolean(values.customizeViewModes, "customizeViewModes")) {
      const availableViewModes = optionalCsv(
        values.availableViewModes,
        "availableViewModes",
        16,
      );
      if (!availableViewModes?.length) {
        throw new TypeError("availableViewModes requires at least one value.");
      }
      const configuredDefault = requiredText(
        values.defaultViewMode,
        "defaultViewMode",
      );
      result.displayConfig = {
        availableViewModes,
        defaultViewMode: availableViewModes.includes(configuredDefault)
          ? configuredDefault
          : availableViewModes[0]!,
      };
    }
    return result;
  }
  if (action === "ontology-update") {
    const changes: JsonObject = {
      displayName: requiredText(values.displayName, "displayName"),
    };
    includeText(changes, "description", values.description);
    includeText(changes, "bodyKind", values.bodyKind);
    return {
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
      changes,
    };
  }
  if (action === "ontology-marker") {
    const label = requiredText(values.label, "label");
    const expectedVersion = Number(model.markerVersion);
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 0) {
      throw new TypeError("markerVersion is required.");
    }
    const templatesValue = optionalText(values.defaultViewTemplates);
    let defaultViewTemplates: JsonValue[] = [];
    if (templatesValue) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(templatesValue);
      } catch {
        throw new TypeError("defaultViewTemplates must be valid JSON.");
      }
      if (!Array.isArray(parsed))
        throw new TypeError("defaultViewTemplates must be an array.");
      defaultViewTemplates = normalizeOntologyMarkerTemplates(parsed);
    }
    return {
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
      expectedVersion,
      label,
      config: { defaultViewTemplates },
    };
  }
  if (action === "ontology-delete" || action === "view-delete") {
    return {
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
    };
  }
  if (action === "ontology-default-views") {
    const result: JsonObject = { expectedDefinitionVersion: requiredExpectedDefinitionVersion(model) };
    includeText(result, "profile", values.profile);
    return result;
  }
  if (action === "ontology-snapshot") {
    const result: JsonObject = {
      sourceOntologyId: requiredText(
        values.sourceOntologyId,
        "sourceOntologyId",
      ),
    };
    includeText(result, "viewId", values.viewId);
    includeText(result, "name", values.name);
    includeText(result, "indexMode", values.indexMode);
    return result;
  }
  if (action === "ontology-import-jsonl") {
    return {
      managedFileRef: {
        kind: "managed-file",
        id: requiredText(values.managedFileId, "managedFileId"),
      },
    };
  }
  if (
    action === "ontology-import-records-jsonl" ||
    action === "ontology-import-tag-relations-jsonl" ||
    action === "ontology-import-text-column-jsonl" ||
    action === "ontology-import-json-column-jsonl" ||
    action === "ontology-import-relation-column-jsonl"
  ) {
    const result: JsonObject = {
      managedFileRef: {
        kind: "managed-file",
        id: requiredText(values.managedFileId, "managedFileId"),
      },
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
    };
    if (
      action === "ontology-import-records-jsonl" ||
      action === "ontology-import-tag-relations-jsonl"
    ) {
      includeText(result, "mode", values.mode);
    }
    if (action === "ontology-import-records-jsonl") {
      includeText(result, "containerViewId", values.containerViewId);
    }
    if (
      action === "ontology-import-text-column-jsonl" ||
      action === "ontology-import-json-column-jsonl" ||
      action === "ontology-import-relation-column-jsonl"
    ) {
      includeText(result, "columnId", values.columnId);
    }
    return result;
  }
  if (action === "feature-column-create") {
    const result: JsonObject = {
      expectedOntologyDefinitionVersion: requiredModelVersion(
        model,
        "expectedOntologyDefinitionVersion",
      ),
      name: requiredText(values.name, "name"),
    };
    includeText(result, "type", values.type);
    const config = optionalJsonObject(values.config, "config");
    if (config !== undefined) result.config = config;
    const sort = optionalNonnegativeInteger(values.sort, "sort");
    if (sort !== undefined) result.sort = sort;
    includeText(result, "scopeContainerViewId", values.scopeContainerViewId);
    return result;
  }
  if (action === "feature-column-update") {
    const changes: JsonObject = {};
    includeText(changes, "name", values.name);
    includeText(changes, "type", values.type);
    const config = optionalJsonObjectOrNull(values.config, "config");
    if (config !== undefined) changes.config = config;
    const sort = optionalNullableNonnegativeInteger(values.sort, "sort");
    if (sort !== undefined) changes.sort = sort;
    if (Object.keys(changes).length === 0) {
      throw new TypeError("changes must contain at least one field.");
    }
    return {
      columnId: requiredText(values.columnId, "columnId"),
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
      changes,
    };
  }
  if (action === "feature-column-attachment-set") {
    return {
      viewId: requiredText(values.viewId, "viewId"),
      columnId: requiredText(values.columnId, "columnId"),
      attached: requiredBoolean(values.attached, "attached"),
      expectedViewDefinitionVersion: requiredModelVersion(
        model,
        "expectedViewDefinitionVersion",
      ),
    };
  }
  if (action === "view-export-jsonl") {
    const result: JsonObject = {
      viewId: requiredText(values.viewId, "viewId"),
      expectedViewDefinitionVersion: requiredModelVersion(
        model,
        "expectedViewDefinitionVersion",
      ),
    };
    const pageSize = optionalBoundedInteger(
      values.pageSize,
      "pageSize",
      100,
      5000,
    );
    if (pageSize !== undefined) result.pageSize = pageSize;
    const tagGroupIds = optionalCsv(values.tagGroupIds, "tagGroupIds", 200);
    if (tagGroupIds) result.tagGroupIds = tagGroupIds;
    const featureColumnIds = optionalCsv(
      values.featureColumnIds,
      "featureColumnIds",
      200,
    );
    if (featureColumnIds) result.featureColumnIds = featureColumnIds;
    const basicFieldIds = optionalCsv(values.basicFieldIds, "basicFieldIds", 2);
    if (
      basicFieldIds?.some(
        (field) => field !== "preview" && field !== "thumbnail",
      )
    ) {
      throw new TypeError(
        'basicFieldIds may contain only "preview" and "thumbnail".',
      );
    }
    if (basicFieldIds) result.basicFieldIds = basicFieldIds;
    return result;
  }
  if (action === "feature-column-images-export") {
    const columnType = optionalText(values.columnType) ?? "feature";
    if (columnType !== "feature" && columnType !== "preview") {
      throw new TypeError('columnType must be "feature" or "preview".');
    }
    const result: JsonObject = {
      viewId: requiredText(values.viewId, "viewId"),
      expectedViewDefinitionVersion: requiredModelVersion(
        model,
        "expectedViewDefinitionVersion",
      ),
      columnType,
    };
    if (columnType === "feature") {
      result.featureColumnId = requiredText(
        values.featureColumnId,
        "featureColumnId",
      );
    } else {
      includeText(result, "featureColumnId", values.featureColumnId);
    }
    const pageSize = optionalBoundedInteger(
      values.pageSize,
      "pageSize",
      100,
      1000,
    );
    if (pageSize !== undefined) result.pageSize = pageSize;
    const itemLimit = optionalBoundedInteger(
      values.itemLimit,
      "itemLimit",
      1,
      1_000_000,
    );
    if (itemLimit !== undefined) result.itemLimit = itemLimit;
    return result;
  }
  if (action === "view-share") {
    const operation = requiredText(values.shareOperation, "shareOperation");
    if (operation !== "upsert" && operation !== "revoke") {
      throw new TypeError('shareOperation must be "upsert" or "revoke".');
    }
    const targetType = requiredText(values.targetType, "targetType");
    if (targetType !== "user" && targetType !== "team") {
      throw new TypeError('targetType must be "user" or "team".');
    }
    const result: JsonObject = {
      expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
      operation,
      targetType,
      targetId: requiredText(values.targetId, "targetId"),
    };
    const permissions = commaSeparatedValues(values.permissions);
    if (permissions.length) result.permissions = permissions;
    const expiresAt = optionalNonnegativeInteger(values.expiresAt, "expiresAt");
    if (expiresAt !== undefined) result.expiresAt = expiresAt;
    return result;
  }
  const changes: JsonObject = {
    name: requiredText(values.name, "name"),
  };
  includeText(changes, "description", values.description);
  includeText(changes, "parentId", values.parentId);
  includeText(changes, "viewType", values.viewType);
  includeText(changes, "sourceContainerViewId", values.sourceContainerViewId);
  const displayConfig = optionalJsonObject(
    values.displayConfig,
    "displayConfig",
  );
  if (displayConfig !== undefined) changes.displayConfig = displayConfig;
  const sort = optionalNonnegativeInteger(values.sort, "sort");
  if (sort !== undefined) changes.sort = sort;
  if (action === "view-create") return changes;
  return {
    expectedDefinitionVersion: requiredExpectedDefinitionVersion(model),
    changes,
  };
};

const modelAction = (value: JsonValue | undefined) =>
  typeof value === "string" &&
  ONTOLOGY_MANAGEMENT_ACTIONS.includes(value as OntologyManagementAction)
    ? (value as OntologyManagementAction)
    : undefined;

const modelCopy = (
  value: JsonValue | undefined,
  key: JsonValue | undefined,
  translate: CapabilityTranslate,
  fallback: string,
): ReactNode => {
  const semanticKey = optionalText(key);
  if (semanticKey) return translate(semanticKey);
  return optionalText(value) ?? translate(fallback);
};

const formValues = (form: HTMLFormElement) =>
  Array.from(form.elements).reduce<Record<string, string>>(
    (values, element) => {
      if (
        !("name" in element) ||
        !("value" in element) ||
        !("disabled" in element)
      ) {
        return values;
      }
      const control = element as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;
      if (!control.name || control.disabled) return values;
      if (control instanceof HTMLInputElement && control.type === "checkbox") {
        if (control.value === "on") {
          values[control.name] = String(control.checked);
        } else if (control.checked) {
          values[control.name] = values[control.name]
            ? `${values[control.name]},${control.value}`
            : control.value;
        }
        return values;
      }
      values[control.name] = control.value;
      return values;
    },
    {},
  );

const Field = ({
  id,
  name,
  label,
  value,
  placeholder,
  required = false,
  textarea = false,
  disabled = false,
  onChange,
}: {
  id: string;
  name: string;
  label: ReactNode;
  value?: string | number;
  placeholder?: string;
  required?: boolean;
  textarea?: boolean;
  disabled?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}) => (
  <BaseField label={label} htmlFor={id} required={required}>
    {textarea ? (
      <BaseTextarea
        id={id}
        name={name}
        defaultValue={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onChange={onChange}
      />
    ) : (
      <BaseInput
        id={id}
        name={name}
        defaultValue={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onChange={onChange}
      />
    )}
  </BaseField>
);

const ManagementFields = ({
  action,
  contextId,
  defaults,
  markerPresets,
  newMarkerTemplate,
  translate,
  disabled,
  tagCatalog,
}: {
  action: OntologyManagementAction;
  contextId: string;
  defaults: JsonObject;
  markerPresets: JsonObject[];
  newMarkerTemplate?: JsonObject;
  translate: CapabilityTranslate;
  disabled: boolean;
  tagCatalog?: MarkerTagCatalog;
}) => {
  const id = (name: string) => `ontology-management-${contextId}-${name}`;
  type FieldKey =
    | "displayName"
    | "label"
    | "templateKey"
    | "templateName"
    | "templateDescription"
    | "templateTags"
    | "creatorCurrentUser"
    | "deleteTemplate"
    | "deleteMarker"
    | "techpackPreset"
    | "trendPreset"
    | "addTemplate"
    | "noTemplates"
    | "defaultViewTemplates"
    | "markerExists"
    | "targetOntology"
    | "descriptionField"
    | "createDefaultViews"
    | "bodyKind"
    | "visible"
    | "kernelReadAllTeams"
    | "publicRead"
    | "publicWrite"
    | "customizeViewModes"
    | "availableViewModes"
    | "defaultViewMode"
    | "viewModeTable"
    | "viewModeCard"
    | "viewModeGallery"
    | "viewModeTrendMatrix"
    | "selectAtLeastOneViewMode"
    | "profile"
    | "sourceOntologyId"
    | "viewId"
    | "name"
    | "indexMode"
    | "parentId"
    | "viewType"
    | "viewTypeFilter"
    | "viewTypeContainer"
    | "sourceContainerViewId"
    | "displayConfig"
    | "sort"
    | "managedFileId"
    | "mode"
    | "columnId"
    | "containerViewId"
    | "type"
    | "config"
    | "scopeContainerViewId"
    | "attached"
    | "attachedTrue"
    | "attachedFalse"
    | "pageSize"
    | "tagGroupIds"
    | "featureColumnIds"
    | "basicFieldIds"
    | "columnType"
    | "columnTypeFeature"
    | "columnTypePreview"
    | "featureColumnId"
    | "itemLimit"
    | "shareOperation"
    | "shareOperationUpsert"
    | "shareOperationRevoke"
    | "targetType"
    | "targetTypeUser"
    | "targetTypeTeam"
    | "targetId"
    | "permissions"
    | "expiresAt";
  const label = (name: FieldKey) =>
    translate(DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement[name]);
  const configuredViewModeOptions = Array.isArray(defaults.viewModeOptions)
    ? defaults.viewModeOptions.flatMap((option) => {
        if (!isJsonObject(option)) return [];
        const id = optionalText(option.id);
        const labelKey = optionalText(option.labelKey);
        return id && labelKey ? [{ id, labelKey }] : [];
      })
    : [];
  const viewModeOptions = configuredViewModeOptions.length
    ? configuredViewModeOptions
    : DEFAULT_ONTOLOGY_VIEW_MODE_OPTIONS;
  const initialViewModes = Array.isArray(defaults.availableViewModes)
    ? defaults.availableViewModes.filter(
        (value): value is string => typeof value === "string" && Boolean(value),
      )
    : DEFAULT_ONTOLOGY_VIEW_MODES;
  const [publicRead, setPublicRead] = useState(defaults.publicRead === true);
  const [customizeViewModes, setCustomizeViewModes] = useState(
    defaults.customizeViewModes === true,
  );
  const [availableViewModes, setAvailableViewModes] =
    useState(initialViewModes);
  const [defaultViewMode, setDefaultViewMode] = useState(
    optionalText(defaults.defaultViewMode) ?? initialViewModes[0] ?? "",
  );
  const toggleViewMode = (viewMode: string, selected: boolean) => {
    const next = selected
      ? Array.from(new Set([...availableViewModes, viewMode]))
      : availableViewModes.filter((candidate) => candidate !== viewMode);
    setAvailableViewModes(next);
    if (!next.includes(defaultViewMode)) setDefaultViewMode(next[0] ?? "");
  };
  if (action === "ontology-create" || action === "ontology-update") {
    return (
      <>
        <Field
          id={id("display-name")}
          name="displayName"
          label={label("displayName")}
          value={optionalText(defaults.displayName)}
          placeholder={
            action === "ontology-create"
              ? translate(
                  "components.data-management.ontology-name-placeholder",
                )
              : undefined
          }
          required
          disabled={disabled}
        />
        <Field
          id={id("description")}
          name="description"
          label={label("descriptionField")}
          value={optionalText(defaults.description)}
          disabled={disabled}
        />
        {action === "ontology-update" ? (
          <Field
            id={id("body-kind")}
            name="bodyKind"
            label={label("bodyKind")}
            value={optionalText(defaults.bodyKind)}
            disabled={disabled}
          />
        ) : null}
        {action === "ontology-create" ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <BaseSwitch
                name="visible"
                label={label("visible")}
                defaultChecked={defaults.visible === true}
                disabled={disabled}
              />
              <BaseSwitch
                name="kernelReadAllTeams"
                label={label("kernelReadAllTeams")}
                defaultChecked={defaults.kernelReadAllTeams === true}
                disabled={disabled}
              />
              <BaseSwitch
                name="publicRead"
                label={label("publicRead")}
                checked={publicRead}
                disabled={disabled}
                onChange={(event) => setPublicRead(event.currentTarget.checked)}
              />
              {!publicRead ? (
                <input type="hidden" name="publicWrite" value="false" />
              ) : null}
              <BaseSwitch
                name="publicWrite"
                label={label("publicWrite")}
                defaultChecked={publicRead && defaults.publicWrite === true}
                disabled={disabled || !publicRead}
              />
            </div>
            <BaseSwitch
              name="customizeViewModes"
              label={label("customizeViewModes")}
              checked={customizeViewModes}
              disabled={disabled}
              onChange={(event) =>
                setCustomizeViewModes(event.currentTarget.checked)
              }
            />
            {customizeViewModes ? (
              <BaseFieldset
                legend={label("availableViewModes")}
                disabled={disabled}
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  {viewModeOptions.map(({ id: value, labelKey }) => (
                    <BaseCheckbox
                      key={value}
                      name="availableViewModes"
                      value={value}
                      label={translate(labelKey)}
                      checked={availableViewModes.includes(value)}
                      onChange={(event) =>
                        toggleViewMode(value, event.currentTarget.checked)
                      }
                      disabled={disabled}
                    />
                  ))}
                </div>
                <BaseField
                  label={label("defaultViewMode")}
                  htmlFor={id("default-view-mode")}
                >
                  <BaseSelect
                    id={id("default-view-mode")}
                    name="defaultViewMode"
                    value={defaultViewMode}
                    onChange={(event) =>
                      setDefaultViewMode(event.currentTarget.value)
                    }
                    disabled={disabled || availableViewModes.length === 0}
                    options={viewModeOptions
                      .filter(({ id: value }) =>
                        availableViewModes.includes(value),
                      )
                      .map(({ id: value, labelKey }) => ({
                        value,
                        label: translate(labelKey),
                      }))}
                  />
                </BaseField>
                {availableViewModes.length === 0 ? (
                  <p className="text-xs text-destructive-text" role="alert">
                    {label("selectAtLeastOneViewMode")}
                  </p>
                ) : null}
              </BaseFieldset>
            ) : null}
          </>
        ) : null}
      </>
    );
  }
  if (action === "ontology-marker") {
    return (
      <OntologyMarkerEditor
        tagCatalog={tagCatalog}
        contextId={contextId}
        defaults={defaults}
        presets={markerPresets}
        newTemplate={newMarkerTemplate}
        translate={translate}
        disabled={disabled}
      />
    );
  }
  if (action === "ontology-delete" || action === "view-delete") {
    return null;
  }
  if (action === "ontology-default-views") {
    return (
      <Field
        id={id("profile")}
        name="profile"
        label={label("profile")}
        value={optionalText(defaults.profile)}
        disabled={disabled}
      />
    );
  }
  if (action === "ontology-snapshot") {
    return (
      <>
        <Field
          id={id("source-ontology")}
          name="sourceOntologyId"
          label={label("sourceOntologyId")}
          value={optionalText(defaults.sourceOntologyId)}
          required
          disabled={disabled}
        />
        <Field
          id={id("view-id")}
          name="viewId"
          label={label("viewId")}
          value={optionalText(defaults.viewId)}
          disabled={disabled}
        />
        <Field
          id={id("name")}
          name="name"
          label={label("name")}
          value={optionalText(defaults.name)}
          disabled={disabled}
        />
        <Field
          id={id("index-mode")}
          name="indexMode"
          label={label("indexMode")}
          value={optionalText(defaults.indexMode)}
          disabled={disabled}
        />
      </>
    );
  }
  if (action === "ontology-import-jsonl") {
    return (
      <Field
        id={id("managed-file-id")}
        name="managedFileId"
        label={label("managedFileId")}
        value={optionalText(defaults.managedFileId)}
        required
        disabled={disabled}
      />
    );
  }
  if (
    action === "ontology-import-records-jsonl" ||
    action === "ontology-import-tag-relations-jsonl" ||
    action === "ontology-import-text-column-jsonl" ||
    action === "ontology-import-json-column-jsonl" ||
    action === "ontology-import-relation-column-jsonl"
  ) {
    return (
      <>
        <Field
          id={id("managed-file-id")}
          name="managedFileId"
          label={label("managedFileId")}
          value={optionalText(defaults.managedFileId)}
          required
          disabled={disabled}
        />
        {action === "ontology-import-records-jsonl" ||
        action === "ontology-import-tag-relations-jsonl" ? (
          <Field
            id={id("mode")}
            name="mode"
            label={label("mode")}
            value={optionalText(defaults.mode)}
            disabled={disabled}
          />
        ) : null}
        {action === "ontology-import-text-column-jsonl" ||
        action === "ontology-import-json-column-jsonl" ||
        action === "ontology-import-relation-column-jsonl" ? (
          <Field
            id={id("column-id")}
            name="columnId"
            label={label("columnId")}
            value={optionalText(defaults.columnId)}
            disabled={disabled}
          />
        ) : null}
        {action === "ontology-import-records-jsonl" ? (
          <Field
            id={id("container-view-id")}
            name="containerViewId"
            label={label("containerViewId")}
            value={optionalText(defaults.containerViewId)}
            disabled={disabled}
          />
        ) : null}
      </>
    );
  }
  if (
    action === "feature-column-create" ||
    action === "feature-column-update"
  ) {
    return (
      <>
        {action === "feature-column-update" ? (
          <Field
            id={id("column-id")}
            name="columnId"
            label={label("columnId")}
            value={optionalText(defaults.columnId)}
            required
            disabled={disabled}
          />
        ) : null}
        <Field
          id={id("name")}
          name="name"
          label={label("name")}
          value={optionalText(defaults.name)}
          required={action === "feature-column-create"}
          disabled={disabled}
        />
        <Field
          id={id("type")}
          name="type"
          label={label("type")}
          value={optionalText(defaults.type)}
          disabled={disabled}
        />
        <Field
          id={id("config")}
          name="config"
          label={label("config")}
          value={
            defaults.config === null
              ? "null"
              : isJsonObject(defaults.config)
                ? JSON.stringify(defaults.config, null, 2)
                : undefined
          }
          textarea
          disabled={disabled}
        />
        <Field
          id={id("sort")}
          name="sort"
          label={label("sort")}
          value={typeof defaults.sort === "number" ? defaults.sort : undefined}
          disabled={disabled}
        />
        {action === "feature-column-create" ? (
          <Field
            id={id("scope-container-view-id")}
            name="scopeContainerViewId"
            label={label("scopeContainerViewId")}
            value={optionalText(defaults.scopeContainerViewId)}
            disabled={disabled}
          />
        ) : null}
      </>
    );
  }
  if (action === "feature-column-attachment-set") {
    return (
      <>
        <Field
          id={id("view-id")}
          name="viewId"
          label={label("viewId")}
          value={optionalText(defaults.viewId)}
          required
          disabled={disabled}
        />
        <Field
          id={id("column-id")}
          name="columnId"
          label={label("columnId")}
          value={optionalText(defaults.columnId)}
          required
          disabled={disabled}
        />
        <BaseField label={label("attached")} htmlFor={id("attached")} required>
          <BaseSelect
            id={id("attached")}
            name="attached"
            defaultValue={defaults.attached === false ? "false" : "true"}
            required
            disabled={disabled}
            options={[
              { value: "true", label: label("attachedTrue") },
              { value: "false", label: label("attachedFalse") },
            ]}
          />
        </BaseField>
      </>
    );
  }
  if (action === "view-export-jsonl") {
    return (
      <>
        <Field
          id={id("view-id")}
          name="viewId"
          label={label("viewId")}
          value={optionalText(defaults.viewId)}
          required
          disabled={disabled}
        />
        <Field
          id={id("page-size")}
          name="pageSize"
          label={label("pageSize")}
          value={
            typeof defaults.pageSize === "number"
              ? defaults.pageSize
              : undefined
          }
          disabled={disabled}
        />
        <Field
          id={id("tag-group-ids")}
          name="tagGroupIds"
          label={label("tagGroupIds")}
          value={optionalText(defaults.tagGroupIds)}
          disabled={disabled}
        />
        <Field
          id={id("feature-column-ids")}
          name="featureColumnIds"
          label={label("featureColumnIds")}
          value={optionalText(defaults.featureColumnIds)}
          disabled={disabled}
        />
        <Field
          id={id("basic-field-ids")}
          name="basicFieldIds"
          label={label("basicFieldIds")}
          value={optionalText(defaults.basicFieldIds)}
          disabled={disabled}
        />
      </>
    );
  }
  if (action === "feature-column-images-export") {
    return (
      <>
        <Field
          id={id("view-id")}
          name="viewId"
          label={label("viewId")}
          value={optionalText(defaults.viewId)}
          required
          disabled={disabled}
        />
        <BaseField
          label={label("columnType")}
          htmlFor={id("column-type")}
          required
        >
          <BaseSelect
            id={id("column-type")}
            name="columnType"
            defaultValue={optionalText(defaults.columnType) ?? "feature"}
            required
            disabled={disabled}
            options={[
              { value: "feature", label: label("columnTypeFeature") },
              { value: "preview", label: label("columnTypePreview") },
            ]}
          />
        </BaseField>
        <Field
          id={id("feature-column-id")}
          name="featureColumnId"
          label={label("featureColumnId")}
          value={optionalText(defaults.featureColumnId)}
          disabled={disabled}
        />
        <Field
          id={id("page-size")}
          name="pageSize"
          label={label("pageSize")}
          value={
            typeof defaults.pageSize === "number"
              ? defaults.pageSize
              : undefined
          }
          disabled={disabled}
        />
        <Field
          id={id("item-limit")}
          name="itemLimit"
          label={label("itemLimit")}
          value={
            typeof defaults.itemLimit === "number"
              ? defaults.itemLimit
              : undefined
          }
          disabled={disabled}
        />
      </>
    );
  }
  if (action === "view-share") {
    return (
      <>
        <BaseField
          label={label("shareOperation")}
          htmlFor={id("share-operation")}
          required
        >
          <BaseSelect
            id={id("share-operation")}
            name="shareOperation"
            defaultValue={optionalText(defaults.shareOperation) ?? "upsert"}
            required
            disabled={disabled}
            options={[
              { value: "upsert", label: label("shareOperationUpsert") },
              { value: "revoke", label: label("shareOperationRevoke") },
            ]}
          />
        </BaseField>
        <BaseField
          label={label("targetType")}
          htmlFor={id("target-type")}
          required
        >
          <BaseSelect
            id={id("target-type")}
            name="targetType"
            defaultValue={optionalText(defaults.targetType) ?? "user"}
            required
            disabled={disabled}
            options={[
              { value: "user", label: label("targetTypeUser") },
              { value: "team", label: label("targetTypeTeam") },
            ]}
          />
        </BaseField>
        <Field
          id={id("target-id")}
          name="targetId"
          label={label("targetId")}
          value={optionalText(defaults.targetId)}
          required
          disabled={disabled}
        />
        <Field
          id={id("permissions")}
          name="permissions"
          label={label("permissions")}
          value={optionalText(defaults.permissions)}
          disabled={disabled}
        />
        <Field
          id={id("expires-at")}
          name="expiresAt"
          label={label("expiresAt")}
          value={
            typeof defaults.expiresAt === "number"
              ? defaults.expiresAt
              : undefined
          }
          disabled={disabled}
        />
      </>
    );
  }
  return (
    <>
      <Field
        id={id("name")}
        name="name"
        label={label("name")}
        value={optionalText(defaults.name)}
        required
        disabled={disabled}
      />
      <Field
        id={id("description")}
        name="description"
        label={label("descriptionField")}
        value={optionalText(defaults.description)}
        textarea
        disabled={disabled}
      />
      <Field
        id={id("parent-id")}
        name="parentId"
        label={label("parentId")}
        value={optionalText(defaults.parentId)}
        disabled={disabled}
      />
      <BaseField label={label("viewType")} htmlFor={id("view-type")} required>
        <BaseSelect
          id={id("view-type")}
          name="viewType"
          defaultValue={optionalText(defaults.viewType) ?? "filter"}
          required
          disabled={disabled}
          options={[
            { value: "filter", label: label("viewTypeFilter") },
            { value: "container", label: label("viewTypeContainer") },
          ]}
        />
      </BaseField>
      <Field
        id={id("source-container-view-id")}
        name="sourceContainerViewId"
        label={label("sourceContainerViewId")}
        value={optionalText(defaults.sourceContainerViewId)}
        disabled={disabled}
      />
      {action === "view-update" ? (
        <Field
          id={id("display-config")}
          name="displayConfig"
          label={label("displayConfig")}
          value={
            isJsonObject(defaults.displayConfig)
              ? JSON.stringify(defaults.displayConfig, null, 2)
              : undefined
          }
          textarea
          disabled={disabled}
        />
      ) : null}
      <Field
        id={id("sort")}
        name="sort"
        label={label("sort")}
        value={typeof defaults.sort === "number" ? defaults.sort : undefined}
        disabled={disabled}
      />
    </>
  );
};

export const OntologyManagementCapabilityRenderer: DesignCapabilityRenderer = ({
  model,
  loading,
  readOnly,
  retry,
  childrenBySlot,
  translate,
  emitIntent,
}) => {
  const action = modelAction(model.action);
  const resourceKind = optionalText(model.resourceKind);
  const contextId = optionalText(model.contextId);
  if (
    !action ||
    !contextId ||
    (resourceKind !== "ontology" &&
      resourceKind !== "view" &&
      resourceKind !== "feature-column" &&
      resourceKind !== "export")
  ) {
    return (
      <CapabilityStatus
        kind="error"
        code="capability.ontology-management.model.invalid"
        translate={translate}
      />
    );
  }
  const defaults = isJsonObject(model.defaults) ? model.defaults : {};
  const disabled = model.disabled === true || model.submitting === true || loading === true || readOnly === true;
  const closeBlocked = model.submitting === true;
  const markerExists =
    action === "ontology-marker" && defaults.markerExists === true;
  const markerVersion = Number(model.markerVersion);
  const actionTitleKey =
    DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.actions[action];
  return (
    <BaseDialog
      open
      portal
      title={modelCopy(model.title, model.titleKey, translate, actionTitleKey)}
      description={modelCopy(
        model.description,
        model.descriptionKey,
        translate,
        DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.description,
      )}
      footer={childrenBySlot.footer}
      closeLabel={translate(
        optionalText(model.cancelLabelKey) ??
          DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.cancel,
      )}
      closeOnEscape={!closeBlocked}
      closeOnOverlayClick={!closeBlocked}
      classNames={{
        panel:
          action === "ontology-create"
            ? "max-w-sm"
            : action === "ontology-marker"
              ? "max-w-lg"
              : "max-w-2xl",
      }}
      onOpenChange={(open) => {
        if (!open && !closeBlocked) {
          emitIntent("capability.ontology-management.cancel", {});
        }
      }}
    >
      {childrenBySlot.header}
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (disabled) return;
          emitIntent(
            ONTOLOGY_MANAGEMENT_INTENT_NAMES[action],
            buildOntologyManagementIntent(
              action,
              model,
              formValues(event.currentTarget),
            ),
          );
        }}
      >
        <BaseFieldset
          legend={translate(actionTitleKey)}
          classNames={{ legend: "sr-only", content: "mt-0" }}
        >
          <BaseStack gap="var(--global-spacing)">
            <ManagementFields
              key={
                action === "ontology-marker"
                  ? `${contextId}:${markerVersion}`
                  : `${contextId}:${action}`
              }
              action={action}
              contextId={contextId}
              defaults={defaults}
              tagCatalog={typeof model.tagSearch === 'string' ? {
                search: model.tagSearch,
                pagination: isJsonObject(model.pagination) ? model.pagination : undefined,
                onSearch: value => emitIntent('capability.ontology-management.tag-search', { value }),
                onPageChange: page => emitIntent('capability.ontology-management.tags-page', { page }),
              } : undefined}
              markerPresets={
                Array.isArray(model.markerPresets)
                  ? model.markerPresets.filter(isJsonObject)
                  : []
              }
              newMarkerTemplate={
                isJsonObject(model.newMarkerTemplate)
                  ? model.newMarkerTemplate
                  : undefined
              }
              translate={translate}
              disabled={disabled}
            />
            {optionalText(model.errorCode) ? (
              <CapabilityStatus
                kind="error"
                code={String(model.errorCode)}
                translate={translate}
                onRetry={readOnly ? retry : undefined}
              />
            ) : null}
            <BaseToolbar>
              {markerExists ? (
                <>
                  <BaseButton
                    type="button"
                    tone="default"
                    label={translate(
                      DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement
                        .createDefaultViews,
                    )}
                    disabled={disabled}
                    onClick={() =>
                      emitIntent(
                        "capability.ontology-management.ontology.default-views.ensure",
                        { expectedDefinitionVersion: requiredExpectedDefinitionVersion(model) },
                      )
                    }
                  />
                  <BaseButton
                    type="button"
                    tone="danger"
                    label={translate(
                      DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement
                        .deleteMarker,
                    )}
                    disabled={
                      disabled ||
                      !Number.isSafeInteger(markerVersion) ||
                      markerVersion < 1
                    }
                    onClick={() =>
                      emitIntent(
                        "capability.ontology-management.ontology.marker.delete",
                        { expectedDefinitionVersion: requiredExpectedDefinitionVersion(model), expectedVersion: markerVersion },
                      )
                    }
                  />
                </>
              ) : null}
              <BaseButton
                type="button"
                tone="default"
                label={translate(
                  optionalText(model.cancelLabelKey) ??
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.cancel,
                )}
                disabled={closeBlocked}
                onClick={() =>
                  emitIntent("capability.ontology-management.cancel", {})
                }
              />
              <BaseButton
                type="submit"
                tone="primary"
                label={translate(
                  optionalText(model.submitLabelKey) ??
                    DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement.submit,
                )}
                loading={model.submitting === true}
                disabled={disabled}
              />
            </BaseToolbar>
          </BaseStack>
        </BaseFieldset>
      </form>
    </BaseDialog>
  );
};
